import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import clsx from 'clsx';

const VIEWPORT_PADDING = 10;
const CURSOR_OFFSET = 14;

const Tooltip = ({
  children,
  content,
  position = 'top',
  trigger = 'click',
  followCursor = false,
  longPressMs = 450,
  interactive = false,
  hideDelayMs = 180,
  contentClassName = '',
  triggerClassName = '',
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [coords, setCoords] = useState(null);
  const [mounted, setMounted] = useState(false);
  const tooltipRef = useRef(null);
  const triggerRef = useRef(null);
  const longPressTimerRef = useRef(null);
  const hideTimerRef = useRef(null);
  const suppressNextClickRef = useRef(false);
  const lastPointerRef = useRef(null);

  useEffect(() => {
    setMounted(true);
  }, []);

  const clampCoords = useCallback((top, left) => {
    if (!tooltipRef.current) {
      return { top, left };
    }

    const tooltipRect = tooltipRef.current.getBoundingClientRect();
    let nextTop = top;
    let nextLeft = left;

    if (nextLeft < VIEWPORT_PADDING) nextLeft = VIEWPORT_PADDING;
    if (nextLeft + tooltipRect.width > window.innerWidth - VIEWPORT_PADDING) {
      nextLeft = window.innerWidth - tooltipRect.width - VIEWPORT_PADDING;
    }
    if (nextTop < VIEWPORT_PADDING) nextTop = VIEWPORT_PADDING;
    if (nextTop + tooltipRect.height > window.innerHeight - VIEWPORT_PADDING) {
      nextTop = window.innerHeight - tooltipRect.height - VIEWPORT_PADDING;
    }

    return { top: nextTop, left: nextLeft };
  }, []);

  const setCursorCoords = useCallback((clientX, clientY) => {
    lastPointerRef.current = { clientX, clientY };
    setCoords(clampCoords(clientY + CURSOR_OFFSET, clientX + CURSOR_OFFSET));
  }, [clampCoords]);

  const clearLongPressTimer = useCallback(() => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  }, []);

  const clearHideTimer = useCallback(() => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  }, []);

  const hideTooltip = useCallback(() => {
    setIsVisible(false);
    setCoords(null);
  }, []);

  const scheduleHide = useCallback(() => {
    clearHideTimer();
    hideTimerRef.current = setTimeout(hideTooltip, hideDelayMs);
  }, [clearHideTimer, hideDelayMs, hideTooltip]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        tooltipRef.current && 
        !tooltipRef.current.contains(event.target) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target)
      ) {
        setIsVisible(false);
      }
    };

    if (isVisible) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isVisible]);

  useEffect(() => {
    if (isVisible && followCursor && lastPointerRef.current) {
      requestAnimationFrame(() => {
        if (!tooltipRef.current || !lastPointerRef.current) return;

        setCursorCoords(lastPointerRef.current.clientX, lastPointerRef.current.clientY);
      });
      return;
    }

    if (isVisible && triggerRef.current && tooltipRef.current) {
      // Use requestAnimationFrame to ensure DOM measurements are accurate
      requestAnimationFrame(() => {
        if (!triggerRef.current || !tooltipRef.current) return;
        
        const rect = triggerRef.current.getBoundingClientRect();
        const tooltipRect = tooltipRef.current.getBoundingClientRect();
        
        let top = 0;
        let left = 0;
        
        switch (position) {
          case 'top':
            top = rect.top - tooltipRect.height - 8;
            left = rect.left + rect.width / 2 - tooltipRect.width / 2;
            break;
          case 'bottom':
            top = rect.bottom + 8;
            left = rect.left + rect.width / 2 - tooltipRect.width / 2;
            break;
          case 'left':
            top = rect.top + rect.height / 2 - tooltipRect.height / 2;
            left = rect.left - tooltipRect.width - 8;
            break;
          case 'right':
            top = rect.top + rect.height / 2 - tooltipRect.height / 2;
            left = rect.right + 8;
            break;
        }
        
        if (left < VIEWPORT_PADDING) left = VIEWPORT_PADDING;
        if (left + tooltipRect.width > window.innerWidth - VIEWPORT_PADDING) {
          left = window.innerWidth - tooltipRect.width - VIEWPORT_PADDING;
        }
        if (top < VIEWPORT_PADDING) {
          // Switch to bottom if too high
          top = rect.bottom + 8;
        }
        if (top + tooltipRect.height > window.innerHeight - VIEWPORT_PADDING) {
          // Switch to top if too low
          top = rect.top - tooltipRect.height - 8;
        }
        
        setCoords({ top, left });
      });
    }
  }, [followCursor, isVisible, position, setCursorCoords]);

  useEffect(() => {
    return () => {
      clearLongPressTimer();
      clearHideTimer();
    };
  }, [clearHideTimer, clearLongPressTimer]);

  const handleClick = (event) => {
    if (trigger !== 'click') return;

    event.stopPropagation();
    setIsVisible((current) => !current);
  };

  const handleClickCapture = (event) => {
    if (!suppressNextClickRef.current) return;

    suppressNextClickRef.current = false;
    event.preventDefault();
    event.stopPropagation();
    setIsVisible(false);
  };

  const handleMouseEnter = (event) => {
    if (trigger !== 'hover') return;

    clearHideTimer();
    setIsVisible(true);
    if (followCursor) {
      setCursorCoords(event.clientX, event.clientY);
    }
  };

  const handleMouseMove = (event) => {
    if (trigger !== 'hover' || !followCursor) return;

    setCursorCoords(event.clientX, event.clientY);
  };

  const handleMouseLeave = () => {
    if (trigger !== 'hover') return;

    if (interactive) {
      scheduleHide();
      return;
    }

    hideTooltip();
  };

  const handleFocus = () => {
    if (trigger !== 'hover') return;

    clearHideTimer();
    setIsVisible(true);
  };

  const handleBlur = (event) => {
    if (trigger !== 'hover') return;
    if (tooltipRef.current?.contains(event.relatedTarget)) return;

    if (interactive) {
      scheduleHide();
      return;
    }

    hideTooltip();
  };

  const handleTouchStart = (event) => {
    if (trigger !== 'hover') return;

    clearLongPressTimer();
    const touch = event.touches[0];
    if (!touch) return;

    longPressTimerRef.current = setTimeout(() => {
      suppressNextClickRef.current = true;
      setIsVisible(true);
      setCursorCoords(touch.clientX, touch.clientY);
    }, longPressMs);
  };

  const handleTouchEnd = () => {
    clearLongPressTimer();
  };

  const handleTouchMove = () => {
    clearLongPressTimer();
  };

  const tooltipContent = isVisible && (
    <div
      ref={tooltipRef}
      onMouseEnter={interactive ? clearHideTimer : undefined}
      onMouseLeave={interactive ? scheduleHide : undefined}
      className={clsx(
        'fixed z-[9999] p-3 text-sm',
        'bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100',
        'border border-gray-200 dark:border-gray-700',
        'rounded-lg shadow-xl',
        'min-w-[200px] max-w-xs',
        coords ? 'opacity-100 visible transition-opacity duration-200' : 'opacity-0 invisible',
        contentClassName
      )}
      style={coords ? {
        top: `${coords.top}px`,
        left: `${coords.left}px`,
      } : {
        visibility: 'hidden',
        top: '-9999px',
        left: '-9999px'
      }}
    >
      {content}
    </div>
  );

  return (
    <>
      <div
        ref={triggerRef}
        onClick={handleClick}
        onClickCapture={handleClickCapture}
        onMouseEnter={handleMouseEnter}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={handleTouchEnd}
        onTouchMove={handleTouchMove}
        className={clsx("cursor-help inline-flex", triggerClassName)}
      >
        {children}
      </div>
      {mounted && createPortal(tooltipContent, document.body)}
    </>
  );
};

Tooltip.propTypes = {
  children: PropTypes.node.isRequired,
  content: PropTypes.node.isRequired,
  position: PropTypes.oneOf(['top', 'bottom', 'left', 'right']),
  trigger: PropTypes.oneOf(['click', 'hover']),
  followCursor: PropTypes.bool,
  longPressMs: PropTypes.number,
  interactive: PropTypes.bool,
  hideDelayMs: PropTypes.number,
  contentClassName: PropTypes.string,
  triggerClassName: PropTypes.string,
};

export default Tooltip;
